import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { setClassNames } from 'ddc-classnames-js';
import { setNewRelicCustomAttribute } from 'ddc-new-relic';
import { useFlags, useLabels, usePrefs, useRequestData } from 'wsm-common-data';
import { useWindowWidth } from 'wsm-srp-utilities';
import { ErrorBoundary } from 'wsm-error-boundary';
import { useSelector, useDispatch } from 'react-redux';
import { isBrowser } from 'ws-scripts/modules/environment';
import {
	buildYmmtFilterObject,
	filterVehicleAndIncentivePromosByYmmt
} from 'wsm-specials-display';
import { setDealerCodes } from '../features/dealerCodes';
import {
	MIN_COLUMNS_FOR_POSITIONING,
	MAX_NUM_HYUNDAI_ADS,
	MIN_NUM_HYUNDAI_ADS
} from '../global-constants';
import { setOffSiteData } from '../actions/offSiteData';
import { setLayout } from '../actions/layout';
import { selectIsGridLayout } from '../features/layoutSlice';
import VehicleCard from './vehicleCard/VehicleCard';
import VehicleCardSkeleton from './vehicleCard/skeleton/VehicleCardSkeleton';
import ErrorAlert from './ErrorAlert';
import { getShouldShowCompare } from '../utilities/compare';
import { getFlagValue } from '../utilities/flags';
import { addImageParams } from '../utilities/images';
import { getIsSmallScreen } from '../utilities/layout';
import { waitForSomething } from '../utilities/waitForSomething';
import IntersectionWrapper from './IntersectionWrapper';
import {
	mergeInventoryAndCtas,
	mergeAllWithPlaceholders,
	mergeInventoryAndAds,
	mergeInventoryAndSCMPromos
} from '../utilities/createCTAandWIAPICards';
import {
	getMaxColumnsByContainer,
	getNumberOfColumns,
	getPrimaryNonVehicleCardPositions,
	getSCMPromoPositions,
	cardsToShow
} from '../utilities/getCTAandWIAPIPositions';
import { useSCMPromos } from '../hooks';
import { getPageNumber } from '../utilities/pagination';

const VehicleCards = ({
	enableMediaCarousel,
	inventoryObj,
	isLoading,
	isRequesting,
	isTransition,
	loadingDelayed,
	appliedFilters
}) => {
	const dispatch = useDispatch();
	const labels = useLabels();
	const flags = useFlags();
	const {
		enableCompare,
		enableHyundaiAds,
		scmEnabled,
		scmEnableContextualFiltering
	} = usePrefs();
	const { deviceType, windowId } = useRequestData();
	const dealerCodes =
		(isBrowser &&
			window.DDC?.InvData?.getInventoryAccounts &&
			window.DDC?.InvData?.getInventoryAccounts()) ||
		{};

	useEffect(() => {
		// isRequesting is true when facet/filter interaction triggered
		// isLoading is true on initial page load
		// When both are false, dealerCodes data is available to use
		if (!isLoading && !isRequesting) {
			dispatch(setDealerCodes(dealerCodes));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isRequesting, isLoading]);

	// TODO: Remove when we have migrated to using WIS calls
	const isWIS =
		(isBrowser &&
			window.DDC?.InvData?.srpReady &&
			window.DDC?.InvData?.getIsWIS()) ||
		false;

	// Layout
	const { isSmallScreen } = useSelector((state) => state.layout);
	const viewportWidth = useWindowWidth();
	const isSmallScreenCurrent = getIsSmallScreen(viewportWidth);
	// Would probably be better to dispatch this along with layout in Widget.jsx if possible.
	// can't use useWindowWidth in class component however
	useEffect(() => {
		if (isSmallScreen !== isSmallScreenCurrent) {
			dispatch(setLayout({ isSmallScreen: isSmallScreenCurrent }));
		}
	}, [isSmallScreenCurrent]); // eslint-disable-line react-hooks/exhaustive-deps
	const isGridLayout = useSelector((state) => selectIsGridLayout(state));

	// Intersection Observer refs options and callback for intersection wrapper
	const observerRef = useRef(null);
	const observerCallback = useCallback((entries) => {
		const entry = entries[0];
		const { isIntersecting, target } = entry;
		const { uuid, location } = target.dataset;

		if (isIntersecting) {
			// Only dispatch the event if the WIAPI is available
			// and if the intersecting entry has a uuid (skeleton cards initially don't)
			if (isBrowser) {
				if (uuid || location) {
					const isLocation = location && !uuid;
					window.dispatchEvent(
						new CustomEvent('wsInvListingShown', {
							detail: {
								id: uuid || location,
								isLocation
							}
						})
					);
				}
			}
		}
	}, []);
	const srpDelayLoadDistance = getFlagValue(
		flags,
		'srp-delay-load-distance',
		'_toggleSRPDelayLoadDistance'
	);
	setNewRelicCustomAttribute('srpDelayLoadDistance', srpDelayLoadDistance);
	const observerOptions = {
		rootMargin: `${srpDelayLoadDistance}px`
	};

	// CTA + WIAPI cards
	const ctaCardItemlist = useSelector((state) => state.ctaCardItemlist);

	// Featured Promotion
	const featuredPromotionVIN =
		isBrowser && window.DDC?.InvData?.urlParams?.featuredPromotion;
	const [featuredPromotionDom, setFeaturedPromotionDom] = useState(null);

	const srpUseImagePlaceholders = getFlagValue(
		flags,
		'srp-use-image-placeholders',
		'_toggleSRPUseImagePlaceholders'
	);
	setNewRelicCustomAttribute(
		'srpUseImagePlaceholders',
		typeof srpUseImagePlaceholders === 'boolean'
			? srpUseImagePlaceholders.toString()
			: null
	);

	const shouldShowCompare = getShouldShowCompare(enableCompare);

	const dealershipName =
		(isBrowser && window.DDC?.dataLayer?.dealership?.dealershipName) || '';

	// OFFSITE
	const [offSiteDataState, setOffSiteDataState] = useState(
		(isBrowser &&
			window?.DDC?.InvData?.getFacets &&
			window.DDC?.InvData?.getFacets().offSiteData) || {
			offSiteBannerPosition: -1,
			offSiteItemCount: -1
		}
	);
	const { offSiteData } =
		(isBrowser &&
			window.DDC?.InvData?.getFacets &&
			window.DDC?.InvData?.getFacets()) ||
		{};
	let enableOffSiteToggle = false;
	enableOffSiteToggle = offSiteData && offSiteData.offSiteItemCount > -1;
	useEffect(() => {
		if (enableOffSiteToggle) {
			setOffSiteDataState({ ...offSiteData });
			dispatch(setOffSiteData(offSiteData));
		}
	}, [offSiteData, enableOffSiteToggle, dispatch]);
	const [showOffSite, setShowOffSite] = useState(
		(isBrowser && sessionStorage.getItem('showOffSite') && true) || false
	);
	let offSiteBannerPosition;
	if (enableOffSiteToggle) {
		// eslint-disable-next-line prefer-destructuring
		offSiteBannerPosition =
			offSiteDataState.offSiteBannerPosition === -1
				? 0
				: offSiteDataState.offSiteBannerPosition;
	}

	const { incentives, accounts, inventory, pageInfo } = inventoryObj;
	const [hasCarouselImages, setHasCarouselImages] = useState(false);

	/**
	 * Circulates vehicles to find if at least one vehicle has more than one image,
	 * For use such as recognizing what styling to apply to all vehicle cards
	 * @param {Vehicle} vehicles
	 */
	function mayUseCarousel(vehicles) {
		return vehicles.some((vehicle) => vehicle.images.length > 1);
	}
	/**
	 * On prop changes, use temporary inventory check and placeholder check to render set hasCarouselImages appropriately
	 */
	useEffect(() => {
		if (inventory.length && !inventory[0].isPlaceholder) {
			setHasCarouselImages(mayUseCarousel(inventory));
		}
	}, [inventory]);

	const vehicleCardDefaultProps = {
		accounts,
		enableMediaCarousel,
		incentives,
		observerRef,
		pageInfo,
		shouldShowCompare,
		loadingDelayed,
		usePlaceholderImage: srpUseImagePlaceholders,
		hasCarouselImages
	};

	// wait for featured promotion vehicle
	// todo remove and use WIS
	useEffect(() => {
		if (!isLoading && isBrowser && !isWIS) {
			if (featuredPromotionVIN) {
				// Skeleton requires uuid as well as FooterContainer to render properly
				const uuid = window.DDC.InvData?.featuredPromotion?.uuid || '';
				setFeaturedPromotionDom(<VehicleCardSkeleton uuid={uuid} />);
			}
			waitForSomething({
				where: 'DDC.InvData.featuredPromotion',
				doBefore: () => {
					window.DDC.pubsub.subscribe(
						'ws-inv-data/vehicle-request-failed',
						() => {
							setFeaturedPromotionDom(null);
						}
					);
				},
				doAfter: () => {
					const tVehicle = window.DDC.InvData.featuredPromotion;
					if (featuredPromotionVIN) {
						setFeaturedPromotionDom(
							<IntersectionWrapper
								key={tVehicle.uuid}
								observerOptions={observerOptions}
								shouldFullyRender
								onIntersectCallback={observerCallback}
							>
								<VehicleCard
									{...vehicleCardDefaultProps}
									featuredPromotion
									vehicle={tVehicle}
									key={tVehicle.uuid}
								/>
							</IntersectionWrapper>
						);
					}
				},
				onGiveUp: () => {
					setFeaturedPromotionDom(null);
				}
			});
		}
	}, [isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

	// update featuredPromotionDom when layout changes
	// todo remove
	useEffect(() => {
		if (
			!isLoading &&
			featuredPromotionVIN &&
			isBrowser &&
			window.DDC?.InvData?.featuredPromotion
		) {
			const featuredPromotionVehicle =
				window.DDC.InvData.featuredPromotion;
			setFeaturedPromotionDom(
				<IntersectionWrapper
					key={featuredPromotionVehicle.uuid}
					observerOptions={observerOptions}
					shouldFullyRender
					onIntersectCallback={observerCallback}
				>
					<VehicleCard
						{...vehicleCardDefaultProps}
						vehicle={featuredPromotionVehicle}
						featuredPromotion
						key={featuredPromotionVehicle.uuid}
					/>
				</IntersectionWrapper>
			);
		}
	}, [isGridLayout]); // eslint-disable-line react-hooks/exhaustive-deps

	// update featuredPromotionDom when vin is cleared
	// todo remove
	useEffect(() => {
		if (!featuredPromotionVIN) {
			setFeaturedPromotionDom(null);
		}
	}, [featuredPromotionVIN]);

	const createVehicleCard = (vehicle, inventoryIndex) => {
		const { uuid, isPlaceholder, offSite, images = [] } = vehicle;
		// Fully render cards above the fold
		const shouldThisVehicleFullyRender =
			!isPlaceholder &&
			((!isLoading && inventoryIndex === 0 && !isGridLayout) ||
				(inventoryIndex <= 2 && isGridLayout));
		const vehicleCardProps = {
			...vehicleCardDefaultProps,
			inventoryIndex,
			vehicle
		};

		const isFirstOffsiteCard = inventoryIndex === offSiteBannerPosition;

		if (
			enableOffSiteToggle &&
			offSite &&
			!isFirstOffsiteCard &&
			!showOffSite
		) {
			return null;
		}
		const placeholderImageSrc =
			(srpUseImagePlaceholders || shouldThisVehicleFullyRender) &&
			images[0]
				? addImageParams(images[0]?.uri)
				: null;
		return (
			<IntersectionWrapper
				key={uuid}
				observerOptions={observerOptions}
				onIntersectCallback={observerCallback}
				shouldFullyRender={shouldThisVehicleFullyRender}
			>
				<VehicleCard
					{...vehicleCardProps}
					key={uuid}
					placeholderImage={placeholderImageSrc}
					setShowOffSite={
						enableOffSiteToggle &&
						isFirstOffsiteCard &&
						!showOffSite
							? setShowOffSite
							: undefined
					}
				/>
			</IntersectionWrapper>
		);
	};

	// SCM on SRP
	let accountData;
	useSCMPromos(flags);
	const { promosList: promos, personalized } = useSelector(
		(state) => state?.contentCards?.scmPromos
	);

	if (isBrowser && window?.DDC?.InvData?.accountData) {
		accountData = window.DDC.InvData.getAccountData();
	}
	let finalPromos;
	if (
		appliedFilters &&
		appliedFilters.length &&
		promos.length &&
		scmEnableContextualFiltering
	) {
		const flatFilteredValue = buildYmmtFilterObject(appliedFilters);
		finalPromos = filterVehicleAndIncentivePromosByYmmt(
			promos,
			flatFilteredValue
		);
	}

	let cards;
	/**
	 * Method for building list of VehicleCards merged with Ads, Content Cards, and Banner Cards
	 * First it merges Content Cards or Ads with Vehicle Inventory
	 * Second it merges the Banners with the Card list to correctly position banners to take up full row
	 * @returns
	 */
	const buildVehicleCardList = () => {
		const options = { observerOptions, observerCallback, observerRef };
		const showHyundaiAds = enableHyundaiAds === 'true' && isGridLayout;
		const showSCMPromoCards =
			scmEnabled === 'true' && promos && promos.length && accountData;

		// in list view we'll always perform calculations based on our MIN_COLUMNS_FOR_POSITIONING
		const columns = !isGridLayout
			? MIN_COLUMNS_FOR_POSITIONING
			: getNumberOfColumns(
					viewportWidth,
					getMaxColumnsByContainer(isBrowser, windowId)
			  );

		let numCardsToShow = cardsToShow(inventory.length);

		cards = inventory.map((vehicle, vehicleIndex) =>
			createVehicleCard(vehicle, vehicleIndex)
		);

		// if final promos is diff from undefined, which means filtering has happened and scmPromos changed, we use it,
		// even if is an empty array, which could be no promos matching
		const correctPromoList = finalPromos || promos;

		// if numCardsToShow value is more than non vehicle cards we actually have, change numCardsToShow to reflect
		// limits of the different non-vehicle card options
		if (showSCMPromoCards && numCardsToShow > correctPromoList.length) {
			numCardsToShow = correctPromoList.length;
		} else if (showHyundaiAds && numCardsToShow > MAX_NUM_HYUNDAI_ADS) {
			numCardsToShow = MAX_NUM_HYUNDAI_ADS;
		}

		// Positions specific to CTAs and Hyundai Ads
		const primaryPositions = getPrimaryNonVehicleCardPositions(
			inventory.length + numCardsToShow,
			columns
		);

		// positions specific to SCM promos
		const scmPositions = getSCMPromoPositions(
			inventory.length + numCardsToShow,
			columns,
			numCardsToShow
		);

		// ctaCards need all 3 positions to choose where to place it, the others non-vehicle-cards do not so get rid of the middle position
		// this can be removed when we get rid of Hyundai Ads
		if (numCardsToShow === 2 && showHyundaiAds) {
			primaryPositions.splice(1, 1);
		}

		// SCM promos are highest priority. If Hyundai chooses to use them they don't get WIAPI inserted ads.
		// Following in priority are the Hyundai Ads, then CTAs. Non Hyundai dealers cannot have SCM promos and CTAs.
		if (showSCMPromoCards) {
			// insert SCMs based on allowed positions so that no matter the num of promos insertion is bound by allowed positions
			const allowedScmPositions = scmPositions.slice(0, numCardsToShow);

			// There may be more total vehicles in inventory than SCM promos for each page. Once we've shown all promos, we cycle back
			// through
			const pageNumber = getPageNumber(
				pageInfo.pageStart,
				pageInfo.pageSize
			);
			let promosStart = numCardsToShow * (pageNumber - 1);
			let promosEnd = promosStart + numCardsToShow;
			// if the promo position we calculate to start from doesn't exist, we calculate its position by using the circular queue
			// concept. For example, for position 13 when only 5 promos exist, I can loop through the promos twice, then land on 3
			if (promosStart >= correctPromoList.length) {
				promosStart %= correctPromoList.length;
				promosEnd = promosStart + numCardsToShow;
			}

			let promosToInsert = correctPromoList.slice(promosStart, promosEnd);
			// sometimes the first promo we grab is close to the end so we only slice off 2 when we need 5 for example. In that case
			// we can start from the beginning and grab the remaining cards we need.
			if (promosToInsert.length < numCardsToShow) {
				const promosLength = promosToInsert.length;
				promosToInsert = [
					...promosToInsert,
					...correctPromoList.slice(0, numCardsToShow - promosLength)
				];
			}
			cards = mergeInventoryAndSCMPromos(
				cards,
				promosToInsert,
				allowedScmPositions,
				accountData,
				personalized,
				deviceType === 'MOBILE',
				isGridLayout
			);

			// styling fix for SCM Promos in the last position of last row, if the last row of SRP only has one card this
			// swaps the promos position with a vehicle card
			if ((inventory.length + numCardsToShow) % columns === 1) {
				const lastPro = cards.pop();
				const lastVehicle = cards.pop();
				cards.push(lastPro);
				cards.push(lastVehicle);
			}
		} else if (showHyundaiAds) {
			// For Hyundai we must show at least 2 Ads on page. This conditional works while Max num is 3 hyundai ads
			numCardsToShow =
				numCardsToShow >= MAX_NUM_HYUNDAI_ADS
					? MAX_NUM_HYUNDAI_ADS
					: MIN_NUM_HYUNDAI_ADS;

			const allowedHyundaiAdPositions = primaryPositions.slice(
				0,
				numCardsToShow
			);
			cards = mergeInventoryAndAds(
				cards,
				allowedHyundaiAdPositions,
				options
			);
		} else if (ctaCardItemlist.length > 0) {
			// CTAs are the last option for non-vehicle cards allowed on the SRP
			const allowedCtas = ctaCardItemlist.slice(0, numCardsToShow);
			cards = mergeInventoryAndCtas(
				cards,
				allowedCtas,
				primaryPositions,
				options
			);
		}
		cards = mergeAllWithPlaceholders(
			cards,
			columns,
			options,
			inventory.length
		);
	};

	// Build vehicle card list
	if (!cards && inventory[0]?.isPlaceholder) {
		cards = inventory.map((vehicle, vehicleIndex) =>
			createVehicleCard(vehicle, vehicleIndex)
		);
	} else {
		buildVehicleCardList();
	}

	// Featured Promotion feature
	if (
		featuredPromotionVIN &&
		isBrowser &&
		window.DDC?.InvData?.featuredPromotion &&
		cards
	) {
		// remove vehicle if already in list
		// todo remove
		cards = cards.filter(
			(card) => card?.key !== window.DDC.InvData.featuredPromotion.uuid
		);
	}

	return (
		<React.Fragment>
			{enableOffSiteToggle &&
				offSiteBannerPosition === 0 &&
				!showOffSite && (
					<ErrorAlert
						errorLabel={labels
							.get('CLEAR_MESSAGE_OFFSITE')
							.replace('{accountName}', dealershipName)}
					/>
				)}
			<ul
				className={setClassNames([
					isGridLayout ? 'vehicle-card-grid' : 'vehicle-card-list',
					'list-unstyled',
					'transition-property-opacity',
					'transition-duration-sm',
					'transition-timing-function-standard',
					isRequesting || isTransition ? 'opacity-25' : null
				])}
			>
				{featuredPromotionDom && (
					<ErrorBoundary newRelicPageAction="WS INV LISTING FEATURED PROMOTION ERROR BOUNDARY">
						{featuredPromotionDom}
					</ErrorBoundary>
				)}
				{cards}
			</ul>
		</React.Fragment>
	);
};

VehicleCards.propTypes = {
	enableMediaCarousel: PropTypes.bool.isRequired,
	isLoading: PropTypes.bool.isRequired,
	isRequesting: PropTypes.bool.isRequired,
	isTransition: PropTypes.bool.isRequired,
	inventoryObj: PropTypes.shape({
		incentives: PropTypes.shape({}),
		accounts: PropTypes.shape({}),
		pageInfo: PropTypes.shape({
			pageSize: PropTypes.number,
			totalCount: PropTypes.number,
			pageStart: PropTypes.number
		}),
		inventory: PropTypes.arrayOf(
			PropTypes.shape({
				images: PropTypes.arrayOf({}),
				isPlaceholder: PropTypes.bool
			})
		)
	}).isRequired,
	appliedFilters: PropTypes.arrayOf({}).isRequired,
	loadingDelayed: PropTypes.bool.isRequired
};

export default VehicleCards;
